import React, { useState } from "react";
import {
  TrendingUp,
  Shield,
  Landmark,
  Code2,
  ShoppingBag,
  Construction,
  CheckCircle2,
} from "lucide-react";
import { NOTIFY_ENDPOINT } from "../config";

const services = [
  {
    Icon: TrendingUp,
    name: "Mutual Fund Distribution",
    desc: "SIP investments, lumpsum, and goal-based planning",
  },
  {
    Icon: Shield,
    name: "Insurance Solutions",
    desc: "Life, health, motor, and comprehensive coverage",
  },
  {
    Icon: Landmark,
    name: "Loan Services",
    desc: "Home, personal, business, and gold loans",
  },
  {
    Icon: Code2,
    name: "Technology Solutions",
    desc: "Fintech apps and automation. Now building a personal finance dashboard for individuals.",
    active: true,
  },
  {
    Icon: ShoppingBag,
    name: "Money Merchandise",
    desc: "Planners, posters, stickers, and kids' piggy banks",
  },
];

export const Services = () => {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — real users never see or fill it
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const submitted = status === "done";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/\S+@\S+\.\S+/.test(email) || status === "sending") return;
    if (!NOTIFY_ENDPOINT) {
      setStatus("error");
      return;
    }
    setStatus("sending");
    try {
      // text/plain keeps this a "simple" CORS request, which Apps Script accepts.
      const res = await fetch(NOTIFY_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "text/plain;charset=utf-8" },
        body: JSON.stringify({ email: email.trim(), website, source: "services-notify" }),
      });
      const data = await res.json();
      setStatus(data.ok ? "done" : "error");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section
      id="services"
      className="bg-white py-20"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span
            className="inline-block rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider"
            style={{ backgroundColor: "#E1F5EE", color: "#0F6E56" }}
            data-testid="services-eyebrow"
          >
            Portfolio
          </span>
          <h2
            className="font-display font-bold mt-4"
            style={{ color: "#1A202C", fontSize: 32, letterSpacing: "-0.01em" }}
            data-testid="services-heading"
          >
            Our Services
          </h2>
          <p className="mt-3" style={{ color: "#718096", fontSize: 16 }}>
            Five service areas. Technology Solutions is live; the rest launch as each licence and partnership comes through.
          </p>
        </div>

        {/* Grid */}
        {/* Flex-wrap (not grid) so a short last row is centred rather than left-aligned. */}
        <div className="flex flex-wrap justify-center gap-6">
          {services.map((s) => {
            const { Icon } = s;
            return (
              <div
                key={s.name}
                className={`relative w-full sm:w-[calc(50%-12px)] lg:w-[calc((100%-48px)/3)] rounded-xl bg-white transition-all duration-200 hover:-translate-y-1 ${s.active ? "" : "bg-construction-stripes"}`}
                style={{
                  border: s.active ? "1px solid #1D9E75" : "1px solid #E2E8F0",
                  padding: "1.5rem",
                  boxShadow: s.active ? "0 4px 16px rgba(29,158,117,0.12)" : "0 1px 2px rgba(0,0,0,0.02)",
                }}
                data-testid={`service-card-${s.name.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
              >
                <div
                  className="rounded-lg flex items-center justify-center"
                  style={{
                    width: 48,
                    height: 48,
                    backgroundColor: "#E1F5EE",
                  }}
                >
                  <Icon size={24} color="#1D9E75" />
                </div>
                <div
                  className="mt-4 font-display font-semibold"
                  style={{ color: "#1A202C", fontSize: 15 }}
                >
                  {s.name}
                </div>
                <p
                  className="mt-1"
                  style={{ color: "#718096", fontSize: 13, lineHeight: 1.55 }}
                >
                  {s.desc}
                </p>
                <div className="mt-5">
                  {s.active ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full"
                      style={{
                        backgroundColor: "#E1F5EE",
                        color: "#0F6E56",
                        fontSize: 11,
                        padding: "4px 10px",
                        fontWeight: 600,
                      }}
                    >
                      <CheckCircle2 size={12} /> Live
                    </span>
                  ) : (
                    <span
                      className="inline-block rounded-full"
                      style={{
                        backgroundColor: "#FAEEDA",
                        color: "#854F0B",
                        fontSize: 11,
                        padding: "4px 10px",
                        fontWeight: 600,
                      }}
                    >
                      Coming Soon
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Under construction banner */}
        <div className="mt-16 flex justify-center">
          <div
            className="rounded-2xl text-center"
            style={{
              border: "2px dashed #E2E8F0",
              padding: "3rem 2rem",
              maxWidth: 600,
              width: "100%",
            }}
            data-testid="under-construction-banner"
          >
            <div className="flex justify-center">
              <Construction size={40} color="#1D9E75" />
            </div>
            <h3
              className="font-display font-semibold mt-4"
              style={{ color: "#1A202C", fontSize: 22 }}
            >
              Full Portfolio Launching Soon
            </h3>
            <p
              className="mt-3 mx-auto"
              style={{
                color: "#718096",
                fontSize: 15,
                lineHeight: 1.6,
                maxWidth: 460,
              }}
            >
              We are currently registering all licences and building our complete
              service infrastructure. Subscribe to be notified when we launch.
            </p>

            {!submitted ? (
              <form
                onSubmit={handleSubmit}
                className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
                data-testid="notify-form"
              >
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="flex-1 rounded-lg outline-none transition-shadow"
                  style={{
                    border: "1px solid #E2E8F0",
                    padding: "12px 16px",
                    fontSize: 14,
                    color: "#1A202C",
                    backgroundColor: "#FFFFFF",
                  }}
                  onFocus={(e) =>
                    (e.currentTarget.style.boxShadow = "0 0 0 3px rgba(29,158,117,0.18)")
                  }
                  onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
                  data-testid="notify-email-input"
                />
                <input
                  type="text"
                  name="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="hidden"
                />
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="rounded-lg font-semibold text-white transition-colors disabled:opacity-70"
                  style={{
                    backgroundColor: "#1D9E75",
                    fontSize: 14,
                    padding: "12px 24px",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#0F6E56")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#1D9E75")
                  }
                  data-testid="notify-submit-btn"
                >
                  {status === "sending" ? "Sending…" : "Notify Me"}
                </button>
              </form>
            ) : (
              <div
                className="mt-6 rounded-lg inline-flex items-center gap-2"
                style={{
                  backgroundColor: "#E1F5EE",
                  color: "#0F6E56",
                  fontSize: 14,
                  padding: "12px 18px",
                  fontWeight: 500,
                }}
                data-testid="notify-success"
              >
                <CheckCircle2 size={16} /> You're on the list! We'll notify you at launch.
              </div>
            )}

            {status === "error" && (
              <div className="mt-3" style={{ color: "#C03A2B", fontSize: 13 }} role="alert" data-testid="notify-error">
                Something went wrong. Please try again, or email us at{" "}
                <a href="mailto:hello@metamonk.co" className="underline">hello@metamonk.co</a>.
              </div>
            )}

            <div className="mt-3" style={{ color: "#718096", fontSize: 12 }}>
              We'll only email you about our launch. Unsubscribe anytime. See our{" "}
              <a href="/privacy" className="underline hover:text-[#1D9E75]">Privacy Policy</a>.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
